import React, { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import Jitsi from 'components/Jitsi/Jitsi';
import { broadCastStorage } from 'helpers/utils';
import { decodeMeetingLink, encodeMeetingLink } from 'utils/meetingLinkUtils';
import { ref, get } from 'firebase/database';
import { db } from 'utils/firebase-config';
import { updateRoomStatus } from 'store/services/firebase-broadcast';

interface IBroadCast {
  roomStatus: Function;
  roomUpdate: boolean;
}

interface RoomData {
  pk: number;
  topic: string;
  host: {
    pk: number;
    name: string;
  };
  participants: any[];
}

const BroadCast: FunctionComponent<IBroadCast> = (props: IBroadCast) => {
  const { roomStatus, roomUpdate } = props;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const history = useHistory();
  const location: any = useLocation();
  const params = useParams<{ encodedData?: string }>();
  const [broadCastItem, setBroadCastItem] = useState<RoomData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [closeTime, setCloseTime] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);

  // Test function to verify meeting link utilities
  const testMeetingLink = () => {
    try {
      const testRoom = {
        pk: 123,
        topic: 'Test Room',
        host: { pk: 456 },
        participants: []
      };
      
      const encoded = encodeMeetingLink(testRoom);
      console.log('Test encode result:', encoded);
      
      const decoded = decodeMeetingLink(encoded);
      console.log('Test decode result:', decoded);
      
      alert('Meeting link test successful! Check console for details.');
    } catch (error: any) {
      console.error('Meeting link test failed:', error);
      alert('Meeting link test failed: ' + error.message);
    }
  };

  /**
   * Handles meeting link flow by decoding URL parameters
   */
  const handleMeetingLinkFlow = useCallback(async (encodedData: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Decode the meeting link data (minimal: id + timestamp)
      const linkData = decodeMeetingLink(encodedData);

      const roomId = linkData.id;

      // Fetch full room from Firebase by id
      const snap = await get(ref(db, `rooms/${roomId}`));
      if (!snap.exists()) {
        throw new Error('Room not found');
      }
      const roomRaw = snap.val();

      // Ensure room is currently in VIDEO status
      if (roomRaw?.status !== 'VIDEO') {
        setError('The video call has been finished.');
        setIsLoading(false);
        setTimeout(() => {
          history.push('/      -nearby');
        }, 3000);
        return;
      }

      // Normalize to expected shape
      const room: RoomData = {
        pk: roomRaw.pk ?? roomId,
        topic: roomRaw.topic,
        host: {
          pk: roomRaw.host?.pk,
          name: roomRaw.host?.name
        },
        participants: roomRaw.participants || []
      };

      // Set room data and clean URL
      setBroadCastItem(room);
      history.replace('/call', { selectedBroadCast: room });
      console.log('Meeting link flow completed successfully');
    } catch (error: any) {
      console.error('Meeting link error:', error);
      setError(error.message || 'Unable to join this video');
      
      // For generic errors we can optionally redirect. When error is the finished-message, we have already returned.
    } finally {
      setIsLoading(false);
    }
  }, [history]);

  /**
   * Initializes the component with room data
   */
  useEffect(() => {
    const initializeRoom = () => {
      // Check if room data is in state (existing flow)
      if (location.state?.selectedBroadCast) {
        setBroadCastItem(location.state.selectedBroadCast);
        return;
      }

      // Check if encoded data is in URL params (meeting link flow)
      if (params.encodedData) {
        handleMeetingLinkFlow(params.encodedData);
      } else {
        console.log('No meeting link detected, using existing flow');
        // No meeting link, this might be a direct navigation to /call without data
        // In this case, we should redirect to conversations or show an error
        if (!location.state?.selectedBroadCast) {
          console.log('No room data available, redirecting to conversations');
          history.push('/conversations-nearby');
        }
      }
    };

    initializeRoom();
  }, [location.state, params.encodedData, handleMeetingLinkFlow, history]);

  const handleClose = async () => {
    broadCastStorage.clear();
    
    // Only update room status if user is the host
    if (user.pk === broadCastItem?.host?.pk) {
      try {
        await updateRoomStatus({
          id: broadCastItem?.pk,
          status: 'CHAT',
        });
      } catch (error) {
        console.error('Error updating Firebase:', error);
        // Continue anyway - Django update will still happen via saga
      }
      
      // Update Django backend (async, non-blocking - saga will also update Firebase)
      roomStatus({
        id: broadCastItem?.pk,
        status: 'CHAT',
      });
    }
    
    // Navigate after Firebase update completes (or immediately for non-hosts)
    history.push('/conversations-nearby');
    window.location.reload();
  };

  useEffect(() => {
    if (location.state?.selectedBroadCast?.host?.pk && 
        user.pk !== location.state.selectedBroadCast.host.pk) {
      if (roomUpdate) {
        history.push('/conversations-nearby');
        window.location.reload();
      }
    }
  }, [roomUpdate, location.state, user.pk, history]);

  useEffect(() => {
    const time = localStorage.getItem('start_time');
    if(time === null || time === '') {
      localStorage.setItem('start_time', JSON.stringify(+new Date()));
    }
  }, []);

  // Update elapsed time display in real-time
  useEffect(() => {
    if (!isClosing) return;
    
    const startTime = performance.now();
    const interval = setInterval(() => {
      const elapsed = Math.round(performance.now() - startTime);
      setElapsedTime(elapsed);
    }, 50); // Update every 50ms for smooth display
    
    return () => clearInterval(interval);
  }, [isClosing]);

  // Handle tab/window close - execute handleClose and track time
  // Only triggers on tab/window close, NOT on page reload
  // 
  // Detection method:
  // - Keyboard shortcuts (Ctrl+R, F5, Ctrl+Shift+R) are detected via keydown events
  // - Browser reload button clicks may not always be detected (browser limitation)
  // - For best results, use keyboard shortcuts for reloading
  useEffect(() => {
    let isExecuting = false;
    let isReloading = false;
    let reloadDetectionTimeout: NodeJS.Timeout | null = null;
    
    // Detect reload via pageshow event (fires after reload completes)
    // This helps track reload state
    const handlePageShow = (e: PageTransitionEvent) => {
      // Reset reload flag when page is shown (in case of bfcache restore)
      if (e.persisted) {
        isReloading = false;
        sessionStorage.removeItem('isReloading');
      }
    };
    
    // Detect clicks on browser UI (reload button area)
    const handleMouseDown = (e: MouseEvent) => {
      // If click is in the top 100px of the window (browser UI area)
      // Set a temporary reload flag
      if (e.clientY < 100) {
        // Assume potential reload button click
        sessionStorage.setItem('possibleReload', Date.now().toString());
        console.log('🖱️ Click detected in browser UI area - possible reload');
      }
    };
    
    // Detect reload events (keyboard shortcuts)
    const handleKeyDown = (e: KeyboardEvent) => {
      // Detect Ctrl+R, Ctrl+Shift+R, F5, Cmd+R (Mac)
      const isReloadKey = 
        (e.ctrlKey && e.key === 'r') ||
        (e.ctrlKey && e.shiftKey && e.key === 'R') ||
        (e.metaKey && e.key === 'r') || // Mac Cmd+R
        e.key === 'F5';
      
      if (isReloadKey) {
        isReloading = true;
        // Store in sessionStorage as backup (cleared after beforeunload)
        sessionStorage.setItem('isReloading', 'true');
        console.log('🔄 Reload detected via keyboard - skipping cleanup');
      }
    };
    
    // Detect reload via navigation API when beforeunload fires
    const checkNavigationType = () => {
      // First check sessionStorage (set by keyboard shortcuts)
      if (sessionStorage.getItem('isReloading') === 'true') {
        isReloading = true;
        sessionStorage.removeItem('isReloading');
        console.log('🔄 Reload detected via sessionStorage (keyboard shortcut) - skipping cleanup');
        return;
      }
      
      // Check if there was a recent click in browser UI area (possible reload button)
      const possibleReloadTime = sessionStorage.getItem('possibleReload');
      if (possibleReloadTime) {
        const clickTime = parseInt(possibleReloadTime);
        const now = Date.now();
        // If click was within last 1000ms, likely a reload button click
        if (now - clickTime < 1000) {
          isReloading = true;
          sessionStorage.removeItem('possibleReload');
          console.log('🔄 Reload detected via browser UI click - skipping cleanup');
          return;
        }
        // Clean up old flag
        sessionStorage.removeItem('possibleReload');
      }
    };
    
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if this is a reload event
      checkNavigationType();
      
      if (isReloading) {
        console.log('⏭️  Skipping cleanup - this is a page reload');
        // Don't prevent default for reloads - allow normal reload without alert
        // Don't call e.preventDefault() here - just return to allow reload
        return;
      }
      
      if (isExecuting) return;
      
      // Don't prevent page unload - removed e.preventDefault() to avoid browser confirmation alert
      // The handleClose cleanup will execute without           blocking the user
      
      // Track start time
      const startTime = performance.now();
      isExecuting = true;
      
      console.log('🚪 Tab closing detected - starting handleClose process...');
      console.log(`⏱️  Start time: ${startTime.toFixed(2)}ms`);
      
      // Execute handleClose synchronously (as much as possible)
      // Note: Browser may not wait for async operations in beforeunload
      (async () => {
        try {
          setIsClosing(true);
          setElapsedTime(0);
          
          console.log('🔄 Executing handleClose()...');
          
          // Execute handleClose and wait for it to complete
          await handleClose();
          
          // Calculate elapsed time
          const endTime = performance.now();
          const elapsedTimeMs = Math.round(endTime - startTime);
          setCloseTime(elapsedTimeMs);
          
          console.log(`⏱️  End time: ${endTime.toFixed(2)}ms`);
          console.log(`✅ handleClose completed successfully!`);
          console.log(`⏰ Total time taken: ${elapsedTimeMs}ms`);
          console.log(`📊 Breakdown: Firebase update, Django update, storage cleanup`);
          
          // Store the time for logging
          sessionStorage.setItem('lastCloseTime', elapsedTimeMs.toString());
          sessionStorage.setItem('lastCloseTimestamp', new Date().toISOString());
        } catch (error) {
          const endTime = performance.now();
          const elapsedTimeMs = Math.round(endTime - startTime);
          
          console.error('❌ Error during handleClose:', error);
          console.log(`⏰ Time elapsed before error: ${elapsedTimeMs}ms`);
        } finally {
          isExecuting = false;
          setIsClosing(false);
        }
      })();
    };

    // Alternative: use unload event for guaranteed execution (but limited time)
    // Only execute if not reloading
    const handleUnload = () => {
      if (isReloading) {
        console.log('⏭️  Skipping unload cleanup - this is a page reload');
        return;
      }
      
      const startTime = performance.now();
      console.log('Unload event - executing final cleanup...');
      
      // For critical operations, use synchronous approach or sendBeacon
      try {
        // Clear storage synchronously
        broadCastStorage.clear();
        
        const endTime = performance.now();
        console.log(`Unload cleanup completed in ${Math.round(endTime - startTime)}ms`);
      } catch (error) {
        console.error('Error during unload:', error);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('mousedown', handleMouseDown);
      
      // Clean up timeout if exists
      if (reloadDetectionTimeout) {
        clearTimeout(reloadDetectionTimeout);
      }
    };
  }, [broadCastItem, user]);

  // Loading state for meeting links
  if (isLoading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Joining meeting...</p>
      </div>
    );
  }

  // Error state for meeting links
  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Unable to Join Meeting</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">Redirecting to conversations...</p>
        </div>
      </div>
    );
  }

  // No room data - show error for meeting links
  if (!broadCastItem || !broadCastItem.pk) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">No Room Data</h4>
          <p>Unable to load room information.</p>
          <p><strong>Debug Info:</strong></p>
          <ul style={{ textAlign: 'left' }}>
            <li>Params: {JSON.stringify(params)}</li>
            <li>Location State: {JSON.stringify(location.state)}</li>
            <li>Current Path: {window.location.pathname}</li>
          </ul>
          <div style={{ marginTop: '20px' }}>
            <button 
              className="btn btn-secondary me-2"
              onClick={testMeetingLink}
            >
              Test Meeting Link
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => history.push('/conversations-nearby')}
            >
              Browse Rooms
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Jitsi handleClose={handleClose} broadCast={broadCastItem} user={user} />
      
      {/* Closing modal with time display */}
      {isClosing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          color: 'white'
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            padding: '40px 50px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(0, 174, 239, 0.2)',
            minWidth: '320px'
          }}>
            <div className="spinner-border text-primary mb-3" role="status" style={{
              width: '3rem',
              height: '3rem'
            }}>
              <span className="sr-only">Processing...</span>
            </div>
            <h4 style={{ 
              marginBottom: '15px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              {closeTime > 0 ? '✅ Room Closed' : '🚪 Closing Room...'}
            </h4>
            
            {closeTime === 0 ? (
              <>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#aaa',
                  marginBottom: '20px'
                }}>
                  Executing cleanup process
                </p>
                <div style={{
                  backgroundColor: 'rgba(0, 174, 239, 0.1)',
                  padding: '15px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 174, 239, 0.3)'
                }}>
                  <p style={{ 
                    fontSize: '14px', 
                    color: '#00aeef',
                    marginBottom: '5px',
                    fontWeight: '500'
                  }}>
                    Elapsed Time
                  </p>
                  <p style={{ 
                    fontSize: '32px', 
                    color: '#00aeef',
                    fontWeight: 'bold',
                    margin: '0',
                    fontFamily: 'monospace'
                  }}>
                    {elapsedTime}ms
                  </p>
                </div>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#666',
                  marginTop: '15px',
                  marginBottom: '0'
                }}>
                  • Updating Firebase<br/>
                  • Updating backend<br/>
                  • Clearing storage
                </p>
              </>
            ) : (
              <div style={{
                backgroundColor: 'rgba(0, 200, 100, 0.1)',
                padding: '20px',
                borderRadius: '8px',
                border: '1px solid rgba(0, 200, 100, 0.3)'
              }}>
                <p style={{ 
                  fontSize: '16px', 
                  color: '#00c864',
                  marginBottom: '10px',
                  fontWeight: '600'
                }}>
                  Process Completed Successfully!
                </p>
                <p style={{ 
                  fontSize: '28px', 
                  color: '#00c864',
                  fontWeight: 'bold',
                  margin: '0',
                  fontFamily: 'monospace'
                }}>
                  {closeTime}ms
                </p>
                <p style={{ 
                  fontSize: '12px', 
                  color: '#888',
                  marginTop: '10px',
                  marginBottom: '0'
                }}>
                  Check console for detailed logs
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BroadCast;
